import { useState, useCallback, useMemo } from 'react';
import { OdooVsSaktiService } from '../services/odooVsSaktiService';
import type {
    OdooVsSaktiRecord,
    OdooVsSaktiFilters,
    OdooVsSaktiDivisionGroup,
    OdooVsSaktiForemanGroup,
} from '../types';
import dayjs from 'dayjs';

const computeAcc = (sakti: number, odoo: number) => (odoo > 0 ? sakti / odoo : 0);

const groupData = (records: OdooVsSaktiRecord[]): OdooVsSaktiDivisionGroup[] => {
    const divMap = new Map<number, {
        divisionGroup: OdooVsSaktiDivisionGroup;
        foremanMap: Map<number, OdooVsSaktiForemanGroup>;
    }>();

    for (const record of records) {
        if (!divMap.has(record.division_id)) {
            divMap.set(record.division_id, {
                divisionGroup: {
                    division_id: record.division_id,
                    division_name: record.division_name,
                    estate_name: record.estate_name,
                    foremanGroups: [],
                    total_odoo_ha: 0,
                    total_sakti_ha: 0,
                    ha_acc: 0,
                    total_odoo_jjg: 0,
                    total_sakti_jjg: 0,
                    jjg_acc: 0,
                    total_odoo_brd: 0,
                    total_sakti_brd: 0,
                    brd_acc: 0,
                    overall_acc: 0,
                },
                foremanMap: new Map(),
            });
        }

        const { divisionGroup, foremanMap } = divMap.get(record.division_id)!;

        if (!foremanMap.has(record.foreman_group_id)) {
            foremanMap.set(record.foreman_group_id, {
                foreman_group_id: record.foreman_group_id,
                foreman_group: record.foreman_group,
                foreman_name: record.foreman_name,
                records: [],
                total_odoo_ha: 0,
                total_sakti_ha: 0,
                ha_acc: 0,
                total_odoo_jjg: 0,
                total_sakti_jjg: 0,
                jjg_acc: 0,
                total_odoo_brd: 0,
                total_sakti_brd: 0,
                brd_acc: 0,
                overall_acc: 0,
            });
        }

        const fg = foremanMap.get(record.foreman_group_id)!;
        fg.records.push(record);
        fg.total_odoo_ha += record.odoo_ha_qty;
        fg.total_sakti_ha += record.sakti_ha_qty;
        fg.total_odoo_jjg += record.odoo_jjg_qty;
        fg.total_sakti_jjg += record.sakti_jjg_qty;
        fg.total_odoo_brd += record.odoo_brd_qty;
        fg.total_sakti_brd += record.sakti_brd_qty;

        divisionGroup.total_odoo_ha += record.odoo_ha_qty;
        divisionGroup.total_sakti_ha += record.sakti_ha_qty;
        divisionGroup.total_odoo_jjg += record.odoo_jjg_qty;
        divisionGroup.total_sakti_jjg += record.sakti_jjg_qty;
        divisionGroup.total_odoo_brd += record.odoo_brd_qty;
        divisionGroup.total_sakti_brd += record.sakti_brd_qty;
    }

    const result: OdooVsSaktiDivisionGroup[] = [];

    for (const { divisionGroup, foremanMap } of divMap.values()) {
        divisionGroup.ha_acc = computeAcc(divisionGroup.total_sakti_ha, divisionGroup.total_odoo_ha);
        divisionGroup.jjg_acc = computeAcc(divisionGroup.total_sakti_jjg, divisionGroup.total_odoo_jjg);
        divisionGroup.brd_acc = computeAcc(divisionGroup.total_sakti_brd, divisionGroup.total_odoo_brd);
        divisionGroup.overall_acc = (divisionGroup.ha_acc + divisionGroup.jjg_acc + divisionGroup.brd_acc) / 3;

        for (const fg of foremanMap.values()) {
            fg.ha_acc = computeAcc(fg.total_sakti_ha, fg.total_odoo_ha);
            fg.jjg_acc = computeAcc(fg.total_sakti_jjg, fg.total_odoo_jjg);
            fg.brd_acc = computeAcc(fg.total_sakti_brd, fg.total_odoo_brd);
            fg.overall_acc = (fg.ha_acc + fg.jjg_acc + fg.brd_acc) / 3;
            divisionGroup.foremanGroups.push(fg);
        }

        result.push(divisionGroup);
    }

    return result;
};

export const useOdooVsSakti = () => {
    const today = dayjs().format('YYYYMMDD');
    const [rawData, setRawData] = useState<OdooVsSaktiRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<OdooVsSaktiFilters>({
        start_date: today,
        end_date: today,
    });

    const groupedData = useMemo(() => groupData(rawData), [rawData]);

    const fetchData = useCallback(async (newFilters?: Partial<OdooVsSaktiFilters>) => {
        setLoading(true);
        setError(null);

        try {
            const appliedFilters = newFilters ? { ...filters, ...newFilters } : filters;
            const response = await OdooVsSaktiService.getBkmVsSakti(appliedFilters);

            if (response.status === 'success') {
                setRawData(response.data || []);
                if (newFilters) {
                    setFilters(appliedFilters);
                }
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Gagal mengambil data';
            setError(errorMessage);
            setRawData([]);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    return {
        groupedData,
        loading,
        error,
        filters,
        fetchData,
    };
};
